import { type WidgetViewComponentProps, widget } from "@handlewithcare/react-prosemirror";
import { basename as pathBasename } from "@tauri-apps/api/path";
import { randomUUID } from "crypto";
import { extension, lookup } from "mime-types";
import { Plugin, PluginKey, type Transaction } from "prosemirror-state";
import { type MapResult, dropPoint } from "prosemirror-transform";
import { DecorationSet, type EditorView } from "prosemirror-view";
import { forwardRef } from "react";

import { generateShortUuid } from "@moment/api-collab";
import { schema } from "@moment/prosemirror-schema";
import {
	env,
	exists,
	getDocumentDirPath,
	getFeatureFlag,
	getPageRelatedFilePath,
	getPageRelatedFilesDirPath,
	mkdir,
	readBinaryFile,
	writeBinaryFile,
} from "@moment/state";
import { makeToast } from "@moment/ui";

import {
	extractAllFileUrlsFromHtml,
	parseAllFileUris,
	persistAttachmentsWithReport,
	persistFileAttachmentsWithReport,
	readAllFilesFromHtml,
	readAllFilesFromUri,
} from "~/components/canvas/utils/attachments";
import { Flag } from "~/state/types";

interface ImageDropPluginDropMeta {
	type: "drop";
	payload: { pos: number };
}

interface ImageDropPluginSavedMeta {
	type: "saved";
}

type ImageDropPluginMeta = ImageDropPluginDropMeta | ImageDropPluginSavedMeta;

interface ImageDropPluginState {
	decorations: DecorationSet;
	transactions: Transaction[];
}

export const imageDropPluginKey = new PluginKey<ImageDropPluginState>("moment/image-drop");

function eventCoords(event: MouseEvent) {
	return { left: event.clientX, top: event.clientY };
}

function splitext(filename: string) {
	const parts = filename.split(".");
	const basename = parts.slice(0, parts.length - 1).join(".");
	const ext = parts.length > 1 ? "." + parts[parts.length - 1] : "";
	return [basename, ext] as const;
}

async function toDataArrayBuffer(htmlData: string | undefined, file: File | undefined) {
	if (htmlData) {
		const parsedDoc = new DOMParser().parseFromString(htmlData, "text/html").body;
		// TODO: handle HTML pastes with multiple images?
		const firstChild = parsedDoc.firstElementChild;
		if (!(firstChild instanceof HTMLImageElement)) {
			return;
		}
		const dataUri = firstChild.src;
		const response = await fetch(dataUri);
		const blob = await response.blob();
		const arrayBuffer = await blob.arrayBuffer();
		const basename = firstChild.alt
			? `${encodeURIComponent(firstChild.alt)}-${generateShortUuid()}`
			: randomUUID();
		const mimeType = response.headers.get("content-type");
		const ext = mimeType ? extension(mimeType) : false;
		return {
			name: `${basename}${ext ? `.${ext}` : ""}`,
			contents: arrayBuffer,
		};
	}
	if (file) {
		const [basename, ext] = splitext(file.name);
		return {
			name: `${encodeURIComponent(basename)}-${generateShortUuid()}${ext}`,
			contents: await file.arrayBuffer(),
		};
	}
}

/**
 * Get the MIME type for an image file based on its filename extension.
 */
function getMimeTypeFromFilename(filename: string | undefined): string | undefined {
	return lookup(filename) || undefined;
}

/**
 * Image file extensions we support
 */
const IMAGE_EXTENSIONS = new Set([
	".png",
	".jpg",
	".jpeg",
	".gif",
	".webp",
	".svg",
	".bmp",
	".ico",
]);

/**
 * Check if a file path has an image extension
 */
function isImageFile(filePath: string): boolean {
	const ext = filePath.toLowerCase().match(/\.[^.]+$/)?.[0];
	return ext ? IMAGE_EXTENSIONS.has(ext) : false;
}

/**
 * Read a file from a file:// URI (used on Linux/Tauri where dropped files
 * come as URI lists instead of File objects).
 */
async function readFileFromUri(
	uriList: string
): Promise<{ name: string; contents: ArrayBuffer } | undefined> {
	const filePath = parseAllFileUris(uriList)[0];
	if (!filePath) {
		return undefined;
	}

	return readFileFromPath(filePath);
}

/**
 * Read a file from a local file path.
 */
async function readFileFromPath(
	filePath: string
): Promise<{ name: string; contents: ArrayBuffer } | undefined> {
	// Check if it's an image file
	if (!isImageFile(filePath)) {
		return undefined;
	}

	// Read the file using Tauri's fs API
	const contents = await readBinaryFile(filePath);
	if (!contents) {
		console.error("[imageDropPlugin] Failed to read file:", filePath);
		return undefined;
	}

	// Extract filename from path (cross-platform)
	const filename = await pathBasename(filePath);
	const [basename, ext] = splitext(filename);

	return {
		name: `${encodeURIComponent(basename)}-${generateShortUuid()}${ext}`,
		contents: contents.buffer as ArrayBuffer,
	};
}

/**
 * Extract a file path from HTML and read the file.
 * On Linux/Tauri, dropped files may come as HTML with:
 * - An anchor tag with file:// URL as TEXT CONTENT (common on Linux file managers)
 * - An anchor tag with file:// URL in href attribute
 * - An img tag with file:// URL in src attribute
 */
async function readFileFromHtml(
	htmlData: string
): Promise<{ name: string; contents: ArrayBuffer } | undefined> {
	const filePath = extractAllFileUrlsFromHtml(htmlData)[0];
	if (filePath) {
		return readFileFromPath(filePath);
	}
	return undefined;
}

export async function readAsDataURL(contents: ArrayBuffer, filename?: string) {
	return new Promise<string>((resolve, reject) => {
		const mimeType = getMimeTypeFromFilename(filename);
		const blob = mimeType ? new Blob([contents], { type: mimeType }) : new Blob([contents]);

		const reader = new FileReader();
		function onLoad(event: ProgressEvent<FileReader>) {
			if (!event.target) {
				return reject(new Error("Failed to convert image data to data URL"));
			}

			reader.removeEventListener("load", onLoad);
			resolve(event.target.result as string);
		}
		reader.addEventListener("load", onLoad);

		reader.readAsDataURL(blob);
	});
}

async function calculateImageDimensions(dataUri: string) {
	return new Promise<{ height: number; width: number }>((resolve) => {
		const image = new Image();
		image.addEventListener("load", () => {
			resolve({
				width: image.naturalWidth,
				height: image.naturalHeight,
			});
		});
		image.src = dataUri;
	});
}

async function persistImage(
	view: EditorView,
	documentId: string,
	pageId: string,
	insertPos: number,
	name: string | undefined,
	contents: ArrayBuffer | undefined
) {
	if (!name || !contents) {
		return;
	}

	const dataUri = await readAsDataURL(contents);

	const documentPath = await getDocumentDirPath(documentId);
	const dirPath = await getPageRelatedFilesDirPath(documentPath, pageId);
	const filePath = await getPageRelatedFilePath(documentPath, pageId, name);

	// Make sure the directory exists
	const dirExists = await exists(dirPath);

	if (!dirExists) {
		await mkdir(dirPath);
	}

	await writeBinaryFile(filePath, new Uint8Array(contents));

	const { width, height } = await calculateImageDimensions(dataUri);

	const pos = insertPos;

	const { transactions } = imageDropPluginKey.getState(view.state)!;
	const mapped = transactions.reduce<null | MapResult>(
		(acc, tr) => (acc?.deleted ? acc : tr.mapping.mapResult(acc?.pos ?? pos)),
		null
	);

	const tr = view.state.tr;
	const meta: ImageDropPluginMeta = {
		type: "saved",
	};
	tr.setMeta(imageDropPluginKey, meta);

	if (mapped?.deleted) {
		view.dispatch(tr);
		return;
	}

	const aspectRatio = width / height;

	tr.insert(
		mapped?.pos ?? pos,
		schema.nodes.image.create({
			file: name,
			width: Math.min(width, 600),
			aspectRatio,
		})
	);
	view.dispatch(tr);
}

const ImagePlaceHolder = forwardRef<HTMLDivElement, WidgetViewComponentProps>(
	function ImagePlaceHolder({ widget: _widget, getPos: _getPos, ...props }, ref) {
		return (
			<div ref={ref} {...props}>
				<div className="relative overflow-hidden rounded-md bg-muted h-32 w-full">
					<div className="absolute inset-0 animate-shimmer bg-gradient-to-r from-transparent via-white/10 to-transparent" />
				</div>
			</div>
		);
	}
);

export function imageDropPlugin(documentId: string, pageId: string, collabEnabled: boolean) {
	return new Plugin({
		key: imageDropPluginKey,
		state: {
			init() {
				return { decorations: DecorationSet.empty, transactions: [] as Transaction[] };
			},
			apply(tr, value, _oldState, newState) {
				const meta = tr.getMeta(imageDropPluginKey) as ImageDropPluginMeta | undefined;
				if (!meta) {
					return { ...value, transactions: [...value.transactions, tr] };
				}

				if (meta.type === "drop") {
					return {
						decorations: DecorationSet.create(newState.doc, [
							widget(meta.payload.pos, ImagePlaceHolder, {
								key: "image-placeholder",
							}),
						]),
						transactions: [] as Transaction[],
					};
				}

				if (meta.type === "saved") {
					return { decorations: DecorationSet.empty, transactions: [] as Transaction[] };
				}

				return { decorations: DecorationSet.empty, transactions: [] as Transaction[] };
			},
		},
		props: {
			handlePaste(view, event) {
				if (!env.isDesktopApp || !view.editable) {
					return false;
				}

				if (event.clipboardData?.types.includes("text/rtf")) {
					// Do not convert pasted rtf to image
					return false;
				}

				const clipboardItems = event.clipboardData?.items;
				if (!clipboardItems) {
					return false;
				}

				const allItems = Array.from(clipboardItems);
				const imageItems = allItems.filter((item) => item.type.indexOf("image") !== -1);

				// Non-image file paste — save as attachment(s)
				if (imageItems.length === 0) {
					if (!getFeatureFlag(Flag.EnableDocumentAttachments)) {
						return false;
					}
					const fileItems = allItems.filter((item) => item.kind === "file");
					if (fileItems.length === 0) {
						return false;
					}
					const files = fileItems
						.map((item) => item.getAsFile())
						.filter((f): f is File => f !== null);
					if (files.length === 0) {
						return false;
					}
					void persistFileAttachmentsWithReport(documentId, files);
					return true;
				}

				const item = imageItems[0];

				const file = item!.getAsFile();
				if (!file) {
					return false;
				}

				// Images in collab are not supported yet
				if (collabEnabled) {
					makeToast({
						variant: "error",
						message:
							"Adding images to collaborative documents is not currently supported",
					});
					return false;
				}

				const tr = view.state.tr;

				let insertPos = view.state.selection.from;

				if (view.state.selection.empty) {
					const { $anchor } = view.state.selection;
					const depth = $anchor.depth;
					const node = $anchor.parent;
					if (depth === 1 && node.isTextblock && node.childCount === 0) {
						insertPos = $anchor.before();
						tr.delete($anchor.before(), $anchor.after());
					}
				} else {
					tr.deleteSelection();
				}

				const meta: ImageDropPluginMeta = {
					type: "drop",
					payload: { pos: insertPos },
				};
				tr.setMeta(imageDropPluginKey, meta);

				view.dispatch(tr);

				event.preventDefault();

				void toDataArrayBuffer(undefined, file).then((data) =>
					persistImage(view, documentId, pageId, insertPos, data?.name, data?.contents)
				);

				return true;
			},
			handleDrop(view, event, slice, moved) {
				if (!env.isDesktopApp || !view.editable) {
					return false;
				}

				// If this is an internal move, don't handle it
				if (moved) {
					return false;
				}

				const dataTransfer = event.dataTransfer;
				const types = dataTransfer?.types ?? [];
				const htmlData = dataTransfer?.getData("text/html");
				const files = dataTransfer?.files ? Array.from(dataTransfer.files) : [];
				const file = files[0];
				const uriList = dataTransfer?.getData("text/uri-list");

				// Check if we have any data to work with
				// On Linux/Tauri, check types array since getData might return empty
				const hasUriListType = types.includes("text/uri-list");
				const hasDropData = htmlData || file || uriList || hasUriListType;
				if (!hasDropData) {
					return false;
				}

				const attachmentsEnabled = getFeatureFlag(Flag.EnableDocumentAttachments);

				// Non-image files are normally handled by DocumentShell's
				// capture-phase listener. If that fails, persist here as a
				// fallback and return true to prevent default behavior.
				if (file && !file.type.startsWith("image/")) {
					if (attachmentsEnabled) {
						void persistFileAttachmentsWithReport(documentId, files);
					}
					return true;
				}

				// Early validation for Linux/Tauri paths (uri-list and htmlData)
				if (!file && uriList) {
					const filePath = parseAllFileUris(uriList)[0];
					if (filePath && !isImageFile(filePath)) {
						if (attachmentsEnabled) {
							void readAllFilesFromUri(uriList).then((result) =>
								persistAttachmentsWithReport(
									documentId,
									result.files,
									result.failedCount
								)
							);
						}
						return true;
					}
				}

				if (!file && htmlData && hasUriListType) {
					const filePath = extractAllFileUrlsFromHtml(htmlData)[0];
					if (filePath && !isImageFile(filePath)) {
						if (attachmentsEnabled) {
							void readAllFilesFromHtml(htmlData).then((result) =>
								persistAttachmentsWithReport(
									documentId,
									result.files,
									result.failedCount
								)
							);
						}
						return true;
					}
				}

				// Images in collab are not supported yet
				if (collabEnabled) {
					makeToast({
						variant: "error",
						message:
							"Adding images to collaborative documents is not currently supported",
					});
					return true; // Return true to prevent default behavior
				}

				const eventPos = view.posAtCoords(eventCoords(event));
				if (!eventPos) {
					return false;
				}

				const $mouse = view.state.doc.resolve(eventPos.pos);

				const dropPos = slice
					? (dropPoint(view.state.doc, $mouse.pos, slice) ?? $mouse.pos)
					: $mouse.pos;

				const $dropPos = view.state.doc.resolve(dropPos);
				const insertPos = $dropPos.after(1);

				const tr = view.state.tr;

				const meta: ImageDropPluginMeta = {
					type: "drop",
					payload: { pos: insertPos },
				};
				tr.setMeta(imageDropPluginKey, meta);
				view.dispatch(tr);

				// Handle the drop data
				// On Linux/Tauri, files come as uri-list or embedded in HTML as file:// URLs
				// Priority: 1) File object, 2) uri-list data, 3) HTML with file:// URL, 4) HTML with data URI
				if (file) {
					// Standard web file drop
					void toDataArrayBuffer(undefined, file).then((data) =>
						persistImage(
							view,
							documentId,
							pageId,
							insertPos,
							data?.name,
							data?.contents
						)
					);
				} else if (uriList) {
					// Linux/Tauri: explicit uri-list data
					void readFileFromUri(uriList).then((data) => {
						if (data) {
							persistImage(
								view,
								documentId,
								pageId,
								insertPos,
								data.name,
								data.contents
							);
						} else {
							makeToast({
								variant: "error",
								message: "Failed to read dropped file",
							});
						}
					});
				} else if (htmlData && hasUriListType) {
					// Linux/Tauri: HTML might contain file:// URL, try to extract it
					void readFileFromHtml(htmlData).then((data) => {
						if (data) {
							persistImage(
								view,
								documentId,
								pageId,
								insertPos,
								data.name,
								data.contents
							);
						} else {
							makeToast({
								variant: "error",
								message: "Failed to read dropped file",
							});
						}
					});
				} else if (htmlData) {
					// Standard HTML with data URI (e.g., from other apps)
					void toDataArrayBuffer(htmlData, undefined).then((data) =>
						persistImage(
							view,
							documentId,
							pageId,
							insertPos,
							data?.name,
							data?.contents
						)
					);
				}

				return true;
			},
		},
	});
}
